import type { ChartConfig } from '@/components/ui/chart'
import type { DashboardChartType } from '@/features/admin/analytics/api/dashboard'

// The theme defines five chart colors (--chart-1..5). Colors are never cycled: past five series a pie folds the
// rest into « Autres », and a line or bar chart is shown as a table.
export const MAX_SERIES = 5
export const seriesColor = (index: number) => `var(--chart-${index + 1})`

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

export function formatNumber(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return Number.isInteger(value) ? value.toLocaleString('fr-FR') : value.toFixed(1).replace('.', ',')
}

const tooltipRow = (color: string, label: string, value: string) =>
  '<div class="flex items-center justify-between gap-4">' +
  '<span class="flex items-center gap-2">' +
  '<span class="inline-block size-2.5 shrink-0 rounded-[2px]" style="background:' +
  color +
  '"></span>' +
  '<span class="text-muted-foreground">' +
  escapeHtml(label) +
  '</span></span>' +
  '<span class="text-foreground font-mono font-medium tabular-nums">' +
  value +
  '</span></div>'

const tooltipBox = (inner: string) =>
  '<div class="border-border/50 bg-background grid min-w-36 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">' +
  inner +
  '</div>'

/* ---------- Donut ---------- */

export type DonutDatumType = { key: string; value: number }

export function buildPie(chart: DashboardChartType) {
  const categoryCol = chart.columns[0]
  const valueCol = chart.columns.find((c, i) => i > 0 && !c.endsWith('__contribution')) ?? chart.columns[1]
  const all: DonutDatumType[] = chart.rows
    .map((row) => ({ key: String(row[categoryCol] ?? 'Inconnu'), value: Number(row[valueCol] ?? 0) }))
    .sort((a, b) => b.value - a.value)
  const data =
    all.length > MAX_SERIES
      ? [
          ...all.slice(0, MAX_SERIES - 1),
          { key: 'Autres', value: all.slice(MAX_SERIES - 1).reduce((sum, d) => sum + d.value, 0) },
        ]
      : all
  const config: ChartConfig = {}
  data.forEach((d, index) => {
    config[d.key] = { label: d.key, color: seriesColor(index) }
  })
  return { data, config, total: data.reduce((sum, d) => sum + d.value, 0) }
}

/**
 * Tooltip of a Unovis donut segment, in the shadcn chart style. ChartTooltipContent reads the series from the
 * datum's own keys (it fits XY charts); a donut datum is { key, value }, so it rendered an empty bubble.
 */
export function donutTooltip(config: ChartConfig, total: number) {
  return (segment: { data: DonutDatumType }) => {
    const { key, value } = segment.data
    const entry = config[key]
    const label = typeof entry?.label === 'string' ? entry.label : key
    const share = total > 0 ? ` (${((value / total) * 100).toFixed(1).replace('.', ',')} %)` : ''
    return tooltipBox(tooltipRow(entry?.color ?? seriesColor(0), label, value.toLocaleString('fr-FR') + share))
  }
}

/* ---------- Line & bar (Superset echarts_timeseries_*) ---------- */

// Superset answers these charts with the x-axis column first, then one column per series.
export type XyDatumType = { index: number; values: Array<number | undefined> }

const formatX = (value: unknown, isTime: boolean) => {
  if (value === null || value === undefined) return '—'
  if (isTime && typeof value === 'number')
    return new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
  return String(value)
}

export function buildXyModel(chart: DashboardChartType) {
  const [xCol, ...seriesCols] = chart.columns
  const series = seriesCols.filter((c) => !c.endsWith('__contribution'))
  // A row without an x value (e.g. sessions without a date) cannot be placed on the axis.
  const rows = chart.rows.filter((row) => row[xCol] !== null && row[xCol] !== undefined && row[xCol] !== '')
  const isTime = rows.some((row) => typeof row[xCol] === 'number' && (row[xCol] as number) > 1e11)
  const labels = rows.map((row) => formatX(row[xCol], isTime))
  const data: XyDatumType[] = rows.map((row, index) => ({
    index,
    values: series.map((col) => (typeof row[col] === 'number' ? (row[col] as number) : undefined)),
  }))
  const step = Math.max(1, Math.ceil(labels.length / 8))
  const config: ChartConfig = {}
  series.forEach((name, index) => {
    config[name] = { label: name, color: seriesColor(index) }
  })
  return {
    series,
    labels,
    data,
    config,
    colors: series.map((_, index) => seriesColor(index)),
    accessors: series.map((_, index) => (d: XyDatumType) => d.values[index]),
    tickValues: labels.map((_, index) => index).filter((index) => index % step === 0),
  }
}

export const xyFits = (chart: DashboardChartType) =>
  chart.rows.length > 0 && chart.columns.filter((c) => !c.endsWith('__contribution')).length - 1 <= MAX_SERIES

export function xyTooltip(model: ReturnType<typeof buildXyModel>, d: XyDatumType) {
  const head = '<div class="text-foreground font-medium">' + escapeHtml(model.labels[d.index] ?? '') + '</div>'
  const rows = model.series.map((name, index) => tooltipRow(model.colors[index], name, formatNumber(d.values[index])))
  return tooltipBox(head + rows.join(''))
}
